import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const ReadabilityAnalyzerViewResult = ({result}) => {
  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-success/20 text-success';
    if (score >= 60) return 'bg-warning/20 text-warning';
    return 'bg-error/20 text-error';
  };

  const getGradeLevelIcon = (level) => {
    switch(level?.toLowerCase()?.replace(/_/g, '')) {
      case 'elementary':
        return 'Notebook';
      case 'middleschool':
        return 'Books';
      case 'highschool':
        return 'GraduationCap';
      case 'college':
        return 'Student';
      case 'professional':
        return 'Briefcase';
      default:
        return 'Book';
    }
  };

  const getComplexityColor = (level) => {
    switch(level?.toLowerCase()) {
      case 'simple':
        return 'bg-success/20 text-success';
      case 'moderate':
        return 'bg-warning/20 text-warning';
      case 'professional':
      case 'complex':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Readability Score Section */}
        <div className="space-y-4">
          {/* Score Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Readability Score</h3>
              </div>

              {/* Score Gauge */}
              <div className="p-4 bg-base-200 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Overall Score</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${getScoreColor(result?.analysis?.readability_status?.score)}`}>
                    {result?.analysis?.readability_status?.score}/100
                  </span>
                </div>
                <div className="w-full bg-base-300 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${getScoreColor(result?.analysis?.readability_status?.score)}`}
                    style={{width: `${result?.analysis?.readability_status?.score}%`}}
                  ></div>
                </div>
              </div>

              {/* Grade Level */}
              <div className="mt-3 p-4 bg-base-200 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Reading Level</span>
                  <div className="flex items-center gap-2">
                    {PhosphorIcons[getGradeLevelIcon(result?.analysis?.readability_status?.grade_level)] && 
                      React.createElement(PhosphorIcons[getGradeLevelIcon(result?.analysis?.readability_status?.grade_level)], {
                        size: 16,
                        className: "text-primary"
                      })
                    }
                    <span className="capitalize">{result?.analysis?.readability_status?.grade_level?.replace(/_/g, ' ')}</span>
                  </div>
                </div>
              </div>

              {/* Complexity Level */}
              <div className="mt-3 p-4 bg-base-200 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Complexity</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${getComplexityColor(result?.analysis?.validation?.complexity_level)}`}>
                    {result?.analysis?.validation?.complexity_level}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Suggestions Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-info/10">
                  <PhosphorIcons.Lightbulb size={20} weight="bold" className="text-info"/>
                </div>
                <h3 className="card-title text-lg">Improvement Suggestions</h3>
              </div>

              <div className="space-y-2">
                {result?.analysis?.readability_status?.suggestions?.map((suggestion, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-base-200 rounded-lg">
                    <PhosphorIcons.CaretRight size={16} className="text-primary"/>
                    <span className="text-sm capitalize">{suggestion}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Validation Section */}
        <div className="space-y-4">
          {/* Validation Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.CheckCircle size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Validation</h3>
              </div>

              <div className="space-y-3">
                {/* Validation Status */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      result?.analysis?.validation?.is_valid
                        ? 'bg-success/20 text-success'
                        : 'bg-error/20 text-error'
                    }`}>
                      {result?.analysis?.validation?.is_valid ? 'Valid' : 'Invalid'}
                    </span>
                  </div>
                </div>

                {/* Confidence Score */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Confidence</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                      {Math.round(result?.analysis?.validation?.confidence * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-success"
                      style={{width: `${result?.analysis?.validation?.confidence * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Analysis Timestamp */}
                <div className="p-3 bg-base-200 rounded-lg text-sm">
                  <span className="text-base-content/70">Last Updated:</span>
                  <span className="ml-2">
                    {new Date(result?.metadata?.analysis_timestamp).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadabilityAnalyzerViewResult;